import React, { useEffect, useState } from "react";
import RowLoading from "./RowLoading.tsx";
import EmptyImage from "../../../EmptyImage.tsx";
import { ReporteChequeo, ReporteRow } from "../../../../models/ReporteChequeo.ts";
import RowReporteChequeo from "./RowReporteChequeo.tsx";
import LastRowSummary from "./LastRowSummary.tsx";
import RowHourSum from "./RowHourSum.tsx";
import {
  formatDataForReport,
  BranchReportMeta,
} from "../../../../utils/reporteChequeoUtils.ts";
import { readHoursByBranch } from "../../../../services/branchSchedule.service.ts";
import {
  reporteChequeoTableUi as S,
  REPORTE_CHEQUEO_COLUMN_COUNT,
  REPORTE_CHEQUEO_COLUMN_WIDTHS,
} from "./reporteChequeoTableUi.ts";

const HEADERS = [
  "Chilito",
  "Puesto",
  "Turno",
  "Fecha",
  "Entrada",
  "Salida",
  "Horas trabajadas",
  "Sucursal de registro",
  "Acciones",
];

const parseTimeToMinutes = (time: string): number => {
  const [hours = "3", minutes = "0", seconds = "0"] = String(time || "03:00:00")
    .split(":")
    .map((v) => v.trim());
  const h = Number(hours) || 0;
  const m = Number(minutes) || 0;
  const s = Number(seconds) || 0;
  return h * 60 + m + s / 60;
};

const TableReporteChequeo = ({
  records,
  loading,
  start,
  end,
}: {
  records: ReporteChequeo[];
  loading: boolean;
  start: string;
  end: string;
}) => {
  const [reportData, setReportData] = useState<ReporteRow[]>([]);
  const [branchOperationalMeta, setBranchOperationalMeta] = useState<
    Record<number, BranchReportMeta>
  >({});

  useEffect(() => {
    const loadMetaByBranch = async () => {
      const branchIds = [
        ...new Set(
          (records || [])
            .map((record) => Number(record.branch_id))
            .filter((branchId) => Number.isFinite(branchId) && branchId > 0)
        ),
      ];

      if (branchIds.length === 0) {
        setBranchOperationalMeta({});
        return;
      }

      try {
        const entries = await Promise.all(
          branchIds.map(async (branchId) => {
            const hours = await readHoursByBranch(branchId);
            const nightScheduleEnabled = Boolean(hours?.nightScheduleEnabled);
            const cutoffMinutes = nightScheduleEnabled
              ? parseTimeToMinutes(
                  String(hours?.operationalDayCutoffTime || "03:00:00")
                )
              : 3 * 60;
            return [
              branchId,
              { nightScheduleEnabled, cutoffMinutes },
            ] as const;
          })
        );

        const next: Record<number, BranchReportMeta> = {};
        entries.forEach(([branchId, meta]) => {
          next[branchId] = meta;
        });
        setBranchOperationalMeta(next);
      } catch {
        setBranchOperationalMeta({});
      }
    };

    loadMetaByBranch();
  }, [records]);

  useEffect(() => {
    const input = records || [];
    if (input.length === 0) {
      setReportData([]);
      return;
    }

    const branchIds = [
      ...new Set(
        input
          .map((r) => Number(r.branch_id))
          .filter((id) => Number.isFinite(id) && id > 0)
      ),
    ];

    const effectiveMeta: Record<number, BranchReportMeta> = {};
    for (const id of branchIds) {
      effectiveMeta[id] = branchOperationalMeta[id] ?? {
        cutoffMinutes: 3 * 60,
        nightScheduleEnabled: false,
      };
    }

    setReportData(formatDataForReport(input, start, end, effectiveMeta));
  }, [records, start, end, branchOperationalMeta]);

  return (
    <div style={S.wrapper}>
      <div
        className="reporte-chequeo-table-scroll"
        style={S.scroll}
        role="region"
        aria-label="Reporte reloj checador"
        tabIndex={0}
      >
        <table className="mb-0 reporte-chequeo-table" style={S.table}>
          <colgroup>
            {REPORTE_CHEQUEO_COLUMN_WIDTHS.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead style={S.thead}>
            <tr>
              {HEADERS.map((label, i) => (
                <th
                  key={label}
                  style={{
                    ...S.th,
                    ...(i === 0
                      ? { ...S.thPartner, borderTopLeftRadius: 16 }
                      : {}),
                    ...(i === 1 ? S.thPosition : {}),
                    ...(i === HEADERS.length - 1
                      ? { borderTopRightRadius: 16 }
                      : {}),
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportData.map((record, index) => (
              <React.Fragment
                key={`${record.branch_id}-${record.checkIn?.assistanceCheck_id}-${index}`}
              >
                <RowReporteChequeo
                  record={record}
                  payrollBranchMeta={branchOperationalMeta}
                  allChecks={records}
                />
                <RowHourSum
                  index={index}
                  records={reportData}
                  payrollBranchMeta={branchOperationalMeta}
                  allChecks={records}
                />
              </React.Fragment>
            ))}

            <LastRowSummary records={reportData} />

            {loading &&
              Array.from({ length: 15 }).map((_, index) => (
                <RowLoading key={index} colSpan={REPORTE_CHEQUEO_COLUMN_COUNT} />
              ))}
            {!loading && records.length === 0 && (
              <tr>
                <td colSpan={REPORTE_CHEQUEO_COLUMN_COUNT} style={S.emptyCell}>
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <EmptyImage className="mx-auto w-250px" />
                    <h5
                      className="text-center mt-3 mb-0"
                      style={{ color: "#64748b", fontSize: "1.05rem" }}
                    >
                      No se encontraron registros
                    </h5>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableReporteChequeo;
